/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { Fragment, useState, useEffect, useRef } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

import { updatePhoto } from "src/graphql/mutations";

const initialState = { caption: "" };

/**
 * The EditCaption component
 *
 * @author Thais Watanabe
 */
const EditCaption = (props) => {
  const {
    setIsEditCaptionOpen,
    isEditCaptionOpen,
    selectedPhoto,
    setSelectedPhoto,
  } = props;

  const [formState, setFormState] = useState(initialState);
  const [error, setError] = useState(false);

  const cancelButtonRef = useRef(null);

  /* useEffect */
  useEffect(() => {
    if (selectedPhoto) {
      const selected = {
        caption: selectedPhoto.caption,
      };
      setFormState(selected);
    }

    return () => {
      setFormState(initialState);
    };
  }, [selectedPhoto]);

  /**
   * setInput
   * @param {*} key
   * @param {*} value
   */
  function setInput(key, value) {
    setFormState({ ...formState, [key]: value });
  }

  /**
   * submit
   */
  async function submit() {
    var error = false;

    if (!formState.caption) {
      setError(true);
      error = true;
    } else {
      setError(false);
      error = false;
    }

    if (!error) {
      if (selectedPhoto) {
        update();
      }
    }
  }

  /**
   * closeModal
   */
   function closeModal() {
    setIsEditCaptionOpen(!isEditCaptionOpen);
    setFormState(initialState);
    setSelectedPhoto(null);
  }

  /**
   * update
   */
  async function update() {
    try {
      selectedPhoto.caption = formState.caption;
      selectedPhoto.updatedAt = new Date().toISOString();
      delete selectedPhoto._deleted;
      delete selectedPhoto._lastChangedAt;

      setIsEditCaptionOpen(!isEditCaptionOpen);
      setFormState(initialState);
      setSelectedPhoto(null);

      await API.graphql(
        graphqlOperation(updatePhoto, {
          input: selectedPhoto,
        })
      );

      toast.success("保存しました");
    } catch (err) {
      console.log(err);
      toast.error("エラーが発生しました");
    }
  }

  return (
    <Transition.Root show={isEditCaptionOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={setIsEditCaptionOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full sm:p-6">
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => closeModal()}
                  >
                    <span className="sr-only">閉じる</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <Dialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    キャプション修正
                  </Dialog.Title>
                  <div className="mt-4">
                    {/* caption */}
                    <>
                      <div className="py-2 sm:col-span-2">
                        <label
                          htmlFor="caption"
                          className="block text-sm font-medium text-gray-700"
                        >
                          キャプション
                        </label>
                        <textarea
                          rows="3"
                          value={formState.caption}
                          onChange={(event) =>
                            setInput("caption", event.target.value)
                          }
                          type="text"
                          name="caption"
                          id="caption"
                          className={`${
                            error && formState.caption.length < 1
                              ? "pr-10 border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-50"
                              : "focus:ring-teal-500 focus:border-teal-500 border-gray-300"
                          } block w-full shadow-sm sm:text-sm rounded-md`}
                        />
                      </div>
                      {error && formState.caption.length < 1 && (
                        <p className="mt-2 text-sm text-red-600">
                          「キャプション」を入力してください
                        </p>
                      )}
                    </>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="button"
                    onClick={submit}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-teal-600 text-base font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:col-start-2 sm:text-sm"
                  >
                    保存する
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => closeModal()}
                    ref={cancelButtonRef}
                  >
                    キャンセル
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default EditCaption;
